import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SessionService } from '../../services/session.service';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

interface LinkItem {
  text: string;
  matIcon?: string;
  svg?: string;
  route?: string; // internal routes
  href?: string; // external URLs
  target?: '_blank' | '_self';
}

interface ColumnItem {
  title: string;
  links: LinkItem[];
  isExpanded: boolean;
}

const externalLinkIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>`;

// --- ONL (UPS Capital) FOOTER DATA ---
const ONL_COLUMNS: ColumnItem[] = [
  { title: 'This Site', links: [
      { text: 'Home', svg: '' },
      { text: 'My Wallet', svg: '', route: 'my-wallet/my-wallet-details' }
  ], isExpanded: false },
  { title: 'Customer Service', links: [
      { text: 'Claims', svg: '', href: 'https://upscapital.com/claims/' },
      { text: 'Help & Support', svg: '', href: 'https://upscapital.com/about/help-and-support/' },
      { text: 'Contact Us', svg: '', href: 'https://upscapital.com/about/contact-us/' }
  ], isExpanded: false },
  { title: 'Company', links: [
      { text: 'About Us', svg: '', href: 'https://upscapital.com/about/' },
      { text: 'Offices', svg: '', href: 'https://upscapital.com/about/offices/' }
  ], isExpanded: false },
  { title: 'Other UPS Sites', links: [
      { text: 'Parcel Pro', svg: 'assets/icons/Assets.svg', href: 'https://www.parcelpro.com/us/en/home.html', target: '_blank' },
      { text: 'InsureShield', svg: 'assets/icons/Assets.svg', href: 'https://www.insureshield.com/us/en/home.html', target: '_blank' },
      { text: 'UPS', svg: 'assets/icons/Assets.svg', href: 'https://www.ups.com/us/en/Home.page', target: '_blank' },
      { text: 'UPS Supply Chain Solutions', svg: 'assets/icons/Assets.svg', href: 'https://www.ups.com/us/en/supplychain/Home.page', target: '_blank' },
      { text: 'About UPS', svg: 'assets/icons/Assets.svg', href: 'https://about.ups.com/us/en/home.html', target: '_blank' }
  ], isExpanded: false },
  { title: 'Connect With Us', links: [
      { text: 'Twitter', svg: 'assets/icons/Assets.svg', href: 'https://twitter.com/upscapital', target: '_blank' },
      { text: 'LinkedIn', svg: 'assets/icons/Assets.svg', href: 'https://www.linkedin.com/company/ups-capital', target: '_blank' },
      { text: 'YouTube', svg: 'assets/icons/Assets.svg', href: 'https://www.youtube.com/@InsureShield', target: '_blank' },
      { text: 'Facebook', svg: 'assets/icons/Assets.svg', href: 'https://www.facebook.com/UPSCapitalInsuranceAgency', target: '_blank' }
  ], isExpanded: false }
];

// --- PPI (ParcelPro) FOOTER DATA ---
const PPI_COLUMNS: ColumnItem[] = [
  { title: 'This Site', links: [
      { text: 'Home', svg: '' },
      { text: 'My Wallet', svg: '', route: 'my-wallet/my-wallet-details' }
  ], isExpanded: false },
  { title: 'Customer Service', links: [
      { text: 'Contact Us', svg: '', href: 'https://web.parcelpro.com/support/contact-us' },
      { text: 'Wallet FAQ', svg: '', href: 'https://s3.us-east-1.amazonaws.com/members-pdf.upsc/PP_UPSWallet_FAQ_ClaimsCash.pdf' }
  ], isExpanded: false },
  { title: 'Company', links: [
      { text: 'About Us', svg: '', href: 'https://www.parcelpro.com/us/en/about-us.html' },
      { text: 'Offices', svg: '', href: 'https://web.parcelpro.com/support/contact-us' }
  ], isExpanded: false },
  { title: 'Other UPS Sites', links: [
      { text: 'Parcel Pro', svg: 'assets/icons/Assets.svg', href: 'https://www.parcelpro.com/us/en/home.html', target: '_blank' },
      { text: 'InsureShield', svg: 'assets/icons/Assets.svg', href: 'https://www.insureshield.com/us/en/home.html', target: '_blank' },
      { text: 'UPS', svg: 'assets/icons/Assets.svg', href: 'https://www.ups.com/us/en/Home.page', target: '_blank' },
      { text: 'UPS Supply Chain Solutions', svg: 'assets/icons/Assets.svg', href: 'https://www.ups.com/us/en/supplychain/Home.page', target: '_blank' },
      { text: 'About UPS', svg: 'assets/icons/Assets.svg', href: 'https://about.ups.com/us/en/home.html', target: '_blank' }
  ], isExpanded: false },
  { title: 'Connect With Us', links: [
      { text: 'Twitter', svg: 'assets/icons/Assets.svg', href: 'https://twitter.com/upscapital', target: '_blank' },
      { text: 'LinkedIn', svg: 'assets/icons/Assets.svg', href: 'https://www.linkedin.com/company/ups-capital', target: '_blank' },
      { text: 'YouTube', svg: 'assets/icons/Assets.svg', href: 'https://www.youtube.com/@InsureShield', target: '_blank' },
      { text: 'Facebook', svg: 'assets/icons/Assets.svg', href: 'https://www.facebook.com/UPSCapitalInsuranceAgency', target: '_blank' }
  ], isExpanded: false }
];

// --- Bottom Legal Links ---
const LEGAL_LINKS_ONL: LinkItem[] = [
  { text: 'Technology Agreement', href: 'https://upscapital.com/technology-agreement' },
  { text: 'Website Terms of Use', href: 'https://www.ups.com/us/en/support/shipping-support/legal-terms-conditions.page', target: '_blank' },
  { text: 'Disclosures', href: 'https://upscapital.com/us-product-disclosures/' },
  { text: 'Your California Privacy Rights', href: 'https://www.ups.com/us/en/support/shipping-support/legal-terms-conditions/california-privacy-rights.page', target: '_blank' },
  { text: 'Privacy Notice', href: 'https://upscapital.com/privacy-notice/' },
  { text: 'Cookie Settings', href: 'https://upscapital.com/' },
  { text: 'Do Not Sell or Share My Personal Information', href: 'https://upscapital.com/opt-out/', target: '_blank' }
];

const LEGAL_LINKS_PPI: LinkItem[] = [
  { text: 'Technology Agreement', href: 'https://www.parcelpro.com/us/en/legal/technology-agreement.html' },
  { text: 'Terms of Use', href: 'https://www.parcelpro.com/us/en/legal/website-terms-of-use.html', target: '_blank' },
  { text: 'Product Disclosure', href: 'https://www.parcelpro.com/us/en/legal/product-disclosure.html' },
  { text: 'Privacy Notice', href: 'https://www.parcelpro.com/us/en/legal/privacy-notice.html?_gl=1*16knj7g*_gcl_au*MTU1NjA1MDQ0LjE3NTc1MjYwODg' },
  { text: 'Cookie Settings', href: 'https://upscapital.com/' },
  { text: 'Do Not Sell or Share My Personal Information', href: 'https://upscapital.com/opt-out/', target: '_blank' }
];

// --- NEXT GEN FOOTER DATA ---

const NEXT_GEN_COLUMNS: ColumnItem[] = [
  { title: 'This Site', links: [
      { text: 'Shipping', svg: '', href: 'https://www.ups.com/ship?loc=en_US' },
      { text: 'Tracking', svg: '', href: 'https://www.ups.com/track?loc=en_US' },
      { text: 'Recognize a UPS Employee', svg: 'assets/icons/Assets.svg', href: 'https://about.ups.com/us/en/thank-a-ups-hero.html' },
      { text: 'Communication Preferences', svg: '', href: 'https://www.ups.com/marketingpreferences?loc=en_US#/emailsubscription' }
  ], isExpanded: false },
    { title: 'UPS Sites', links: [
      { text: 'About UPS', svg: 'assets/icons/Assets.svg', href: 'https://about.ups.com/us/en/home.html' },
      { text: 'UPS Jobs', svg: 'assets/icons/Assets.svg', href: 'https://www.jobs-ups.com/us/en' },
      { text: 'UPS Healthcare', svg: '', href: 'https://www.ups.com/us/en/ups-healthcare' },
      { text: 'UPS Supply Chain Solutions', svg: 'assets/icons/Assets.svg', href: 'https://www.ups.com/us/en/ups-supplychain' },
      { text: 'The UPS Store', svg: 'assets/icons/Assets.svg', href: 'https://www.theupsstore.com/' },      
      { text: 'UPS Developer Portal', svg: 'assets/icons/Assets.svg', href: 'https://developer.ups.com/' }
  ], isExpanded: false },
  { title: 'UPS Companies', links: [
      { text: 'Happy Returns', svg: '', href: 'https://www.ups.com/us/en/ups-happyreturns' }, 
      { text: 'Roadie', svg: '', href: 'https://www.ups.com/us/en/ups-roadie' },
      { text: 'InsureShield Shipping Insurance®', svg: '', href: 'https://www.ups.com/us/en/ups-insureshield' },
      { text: 'Parcel Pro<sup>&reg;</sup>', svg: '', href: 'https://www.parcelpro.com/us/en/home.html' },
      { text: 'Delivery Solutions', svg: 'assets/icons/Assets.svg', href: 'https://deliverysolutions.co/' },
  ], isExpanded: false },
  { title: 'Connect with Us', links: [
      { text: 'Facebook', svg: 'assets/icons/Assets.svg', href: 'https://www.facebook.com/ups', target: '_blank' },
      { text: 'X', svg: 'assets/icons/Assets.svg', href: 'https://twitter.com/UPS', target: '_blank' },
      { text: 'Instagram', svg: 'assets/icons/Assets.svg', href: 'https://www.instagram.com/ups', target: '_blank' },
      { text: 'LinkedIn', svg: 'assets/icons/Assets.svg', href: 'https://www.linkedin.com/company/ups', target: '_blank' },
      { text: 'YouTube', svg: 'assets/icons/Assets.svg', href: 'https://www.youtube.com/c/UPS', target: '_blank' }
  ], isExpanded: false }
];

const LEGAL_LINKS_NEXT_GEN: LinkItem[] = [
  { text: 'Global Home', href: 'https://www.ups.com/global.html' },
  { text: 'Protect Against Fraud', href: 'https://www.ups.com/us/en/support/shipping-support/legal-terms-conditions/fight-fraud' },
  { text: 'Terms and Conditions', href: '/terms-conditions' },
  { text: 'Website Terms of Use', href: 'https://www.ups.com/us/en/support/shipping-support/legal-terms-conditions/website-terms-of-use' },
  { text: 'Your California Privacy Rights', href: 'https://www.ups.com/us/en/support/shipping-support/legal-terms-conditions/california-privacy-rights' },  
    { text: 'Privacy Notice', href: 'https://www.ups.com/us/en/support/shipping-support/legal-terms-conditions/privacy-notice' },
     { text: 'Do Not Sell or Share My Personal Information', href: 'https://www.ups.com/ppwa/doWork?loc=en_US' }
];

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss']
})
export class FooterComponent implements OnInit, OnDestroy {
  columns: ColumnItem[] = [];
  bottomLegalLinks: LinkItem[] = [];
  isMobileView = window.innerWidth < 768;
  private routerSubscription: Subscription | undefined;

  constructor(private router: Router, private sessionService: SessionService) {}


@HostListener('window:resize', ['$event'])
onResize(event: Event): void {
  this.isMobileView = window.innerWidth < 768;
}

  toggleColumn(index: number): void {
    if (this.isMobileView) {
      this.columns[index].isExpanded = !this.columns[index].isExpanded;
    }
  }

  ngOnInit(): void {
    this.initializeFooter();

    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd) // Requires 'NavigationEnd' import from @angular/router
    ).subscribe(() => {
      this.initializeFooter();
    });

    // Optional: re-check in case sessionStorage gets populated late
    setTimeout(() => this.initializeFooter(), 800);
  }

  ngOnDestroy(): void {
    // Clean up subscription to prevent memory leaks
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private initializeFooter(): void {
     const currentUrl = this.router.url;
     const isNextGen = currentUrl.includes('next-gen') || currentUrl === '/' || currentUrl.includes('/terms-conditions');
     const isPPIFlag = this.sessionService.isPPI();
     console.log(`Footer Update: URL=${currentUrl}, NextGen=${isNextGen}, isPPI():`, isPPIFlag);

    if (isNextGen) {
      // PRIORITY 1: If URL says Next Gen, show Next Gen Footer
      this.columns = NEXT_GEN_COLUMNS;
      this.bottomLegalLinks = LEGAL_LINKS_NEXT_GEN;
    } else if (isPPIFlag) {
      this.columns = PPI_COLUMNS;
      this.bottomLegalLinks = LEGAL_LINKS_PPI;
    } else {
      this.columns = ONL_COLUMNS;
      this.bottomLegalLinks = LEGAL_LINKS_ONL;
    }
  }
}
